import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ClueCard from "@/components/base/ClueCard";
import { clues } from "@/mocks/clues";
import { categories } from "@/mocks/categories";

function ClueFilterList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const keyword = searchParams.get("q") || "";

  const filtered = useMemo(() => {
    return clues.filter((clue) => {
      const matchCategory = activeCategory === "all" || clue.categorySlug === activeCategory;
      const kw = keyword.trim().toLowerCase();
      const matchKeyword =
        !kw ||
        clue.title.toLowerCase().includes(kw) ||
        clue.summary.toLowerCase().includes(kw) ||
        clue.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <div className="flex flex-col gap-4 border-b border-background-200 pb-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => updateParam("category", "all")}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
              activeCategory === "all"
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            全部
          </button>
          {categories.map((category) => (
            <button
              key={category.slug}
              type="button"
              onClick={() => updateParam("category", category.slug)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                activeCategory === category.slug
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="relative w-full lg:w-72">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base" />
          </span>
          <input
            type="search"
            value={keyword}
            onChange={(event) => updateParam("q", event.target.value)}
            placeholder="搜索线索标题、关键词"
            aria-label="搜索线索"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
      </div>

      <p className="mt-5 text-sm text-foreground-500">
        共找到 <span className="font-medium text-foreground-900">{filtered.length}</span> 条线索
        {keyword && <span>，关键词“{keyword}”</span>}
      </p>

      {filtered.length > 0 ? (
        <div className="mt-5 space-y-4">
          {filtered.map((clue) => (
            <ClueCard key={clue.slug} clue={clue} variant="horizontal" />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-search-line text-2xl" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
            暂时没有匹配的线索
          </p>
          <p className="mt-1 text-sm text-foreground-500">换个关键词，或切换到其他分类看看。</p>
        </div>
      )}
    </div>
  );
}

export default ClueFilterList;